import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchRestaurantMyeatsOffices,
  mapRestaurantMyeatsOffice,
  searchRestaurantMyeatsOffices,
  unmapRestaurantMyeatsOffice,
} from './restaurantApi';
import './css/AgePricingEditor.css';

function orgIdOf(row) {
  return String(row?.organizationId ?? row?.organization_id ?? '').trim();
}

function orgNameOf(row) {
  return String(row?.organizationName ?? row?.organization_name ?? '').trim() || `Org ${orgIdOf(row)}`;
}

/**
 * Map Offices for Work Eats — same idea as Map Schools on pickup hubs,
 * but at restaurant level for workplace orgs.
 */
export default function WorkEatsMapOfficesPanel({
  restaurantId,
  userId,
  disabled = false,
  onMappedOfficesChange,
}) {
  const [mapped, setMapped] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [name, setName] = useState('');
  const [radiusKm, setRadiusKm] = useState('10');
  const [message, setMessage] = useState('');

  const mappedIds = useMemo(
    () => new Set(mapped.map((row) => orgIdOf(row)).filter(Boolean)),
    [mapped],
  );

  const loadMapped = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setMessage('');
    try {
      const offices = await fetchRestaurantMyeatsOffices(restaurantId);
      setMapped(offices);
      onMappedOfficesChange?.(offices);
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to load mapped offices.');
    } finally {
      setLoading(false);
    }
  }, [onMappedOfficesChange, restaurantId]);

  const searchNearby = useCallback(async () => {
    if (!restaurantId) return;
    setSearching(true);
    setMessage('');
    try {
      const offices = await searchRestaurantMyeatsOffices(restaurantId, {
        name: name.trim(),
        radiusKm: Number(radiusKm) || 10,
      });
      setNearby(offices);
      if (!offices.length) {
        setMessage(`No offices found within ${radiusKm || 10} km.`);
      }
    } catch (error) {
      setNearby([]);
      setMessage(error?.response?.data?.error || error?.message || 'Failed to search offices.');
    } finally {
      setSearching(false);
    }
  }, [name, radiusKm, restaurantId]);

  useEffect(() => {
    void loadMapped();
  }, [loadMapped]);

  useEffect(() => {
    if (!restaurantId) return undefined;
    const t = setTimeout(() => {
      void searchNearby();
    }, 350);
    return () => clearTimeout(t);
  }, [restaurantId, name, radiusKm, searchNearby]);

  const handleMap = async (organizationId) => {
    if (!restaurantId || !userId || !organizationId) return;
    setActionId(String(organizationId));
    setMessage('');
    try {
      const data = await mapRestaurantMyeatsOffice(restaurantId, organizationId, userId);
      const offices = Array.isArray(data?.offices) ? data.offices : await fetchRestaurantMyeatsOffices(restaurantId);
      setMapped(offices);
      onMappedOfficesChange?.(offices);
      setNearby((prev) =>
        prev.map((row) =>
          orgIdOf(row) === String(organizationId) ? { ...row, mapped: true } : row,
        ),
      );
      setMessage('Office mapped for Work Eats.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to map office.');
    } finally {
      setActionId(null);
    }
  };

  const handleUnmap = async (organizationId) => {
    if (!restaurantId || !organizationId) return;
    setActionId(String(organizationId));
    setMessage('');
    try {
      const data = await unmapRestaurantMyeatsOffice(restaurantId, organizationId);
      const offices = Array.isArray(data?.offices) ? data.offices : await fetchRestaurantMyeatsOffices(restaurantId);
      setMapped(offices);
      onMappedOfficesChange?.(offices);
      setNearby((prev) =>
        prev.map((row) =>
          orgIdOf(row) === String(organizationId) ? { ...row, mapped: false } : row,
        ),
      );
      setMessage('Office unmapped.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to unmap office.');
    } finally {
      setActionId(null);
    }
  };

  const combinedRows = useMemo(() => {
    const byId = new Map();
    for (const row of mapped) {
      const id = orgIdOf(row);
      if (!id) continue;
      byId.set(id, { ...row, mapped: true });
    }
    for (const row of nearby) {
      const id = orgIdOf(row);
      if (!id) continue;
      const existing = byId.get(id);
      byId.set(id, {
        ...(existing || {}),
        ...row,
        mapped: existing ? true : Boolean(row.mapped || mappedIds.has(id)),
      });
    }
    return [...byId.values()].sort((a, b) => {
      const da = a.distanceKm == null ? Number.POSITIVE_INFINITY : Number(a.distanceKm);
      const db = b.distanceKm == null ? Number.POSITIVE_INFINITY : Number(b.distanceKm);
      if (da !== db) return da - db;
      return orgNameOf(a).localeCompare(orgNameOf(b));
    });
  }, [mapped, mappedIds, nearby]);

  return (
    <div className="age-pricing-editor meal-delivery-editor">
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">Map Offices (Work Eats)</label>
        <p className="age-pricing-editor__hint">
          Link workplace organizations this restaurant delivers to — same idea as Map Schools for
          Jr Eats. After mapping, set per-office delivery and order-cutoff times below. Schools are
          excluded here (use the Map Schools tab for Jr Eats).
        </p>
      </div>

      <div className="get-restaurant-form-row flex" style={{ gap: 8, marginBottom: 10 }}>
        <div className="get-restaurant-form-group">
          <label>Search office name</label>
          <input
            type="text"
            value={name}
            disabled={disabled || searching}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Corp"
          />
        </div>
        <div className="get-restaurant-form-group">
          <label>Radius (km)</label>
          <input
            type="number"
            min="1"
            max="50"
            value={radiusKm}
            disabled={disabled || searching}
            onChange={(e) => setRadiusKm(e.target.value)}
          />
        </div>
        <div className="get-restaurant-form-group" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={disabled || searching || !restaurantId}
            onClick={() => void searchNearby()}
          >
            {searching ? 'Searching…' : 'Search nearby'}
          </button>
        </div>
      </div>

      {message ? <p className="age-pricing-editor__hint">{message}</p> : null}
      {loading ? <p className="age-pricing-editor__hint">Loading mapped offices…</p> : null}

      <div className="age-pricing-editor__table-wrap">
        <table className="age-pricing-editor__table">
          <thead>
            <tr>
              <th>Office</th>
              <th>Type</th>
              <th>Distance</th>
              <th>Address</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {combinedRows.length === 0 ? (
              <tr>
                <td colSpan={5}>No offices yet. Ensure the restaurant has an address with coordinates.</td>
              </tr>
            ) : (
              combinedRows.map((row) => {
                const id = orgIdOf(row);
                const mappedRow = Boolean(row.mapped || mappedIds.has(id));
                const busy = actionId === id;
                return (
                  <tr key={id}>
                    <td>
                      <strong>{orgNameOf(row)}</strong>
                      <div className="age-pricing-editor__hint">ID {id}</div>
                    </td>
                    <td>{row.organizationType || '—'}</td>
                    <td>
                      {row.distanceKm != null && Number.isFinite(Number(row.distanceKm))
                        ? `${Number(row.distanceKm).toFixed(1)} km`
                        : '—'}
                    </td>
                    <td>{row.addressLine || '—'}</td>
                    <td>
                      {mappedRow ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={disabled || busy}
                          onClick={() => void handleUnmap(id)}
                        >
                          {busy ? '…' : 'Remove'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={disabled || busy || !userId}
                          onClick={() => void handleMap(id)}
                        >
                          {busy ? '…' : 'Add'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
